import { EventType } from "@/analytics/analytics-events";
import {
  CancelAnnotationCreationEventProperties,
  CreateAnnotationEventProperties,
} from "@/analytics/annotation-events-properties";
import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import {
  CreateAnnotationForm,
  CreateAnnotationFormProps,
} from "@/components/ui/annotations/create-annotation-form";
import { useCurrentScene } from "@/modes/mode-data-context";
import { useIsPanoWithDepthAndReady } from "@/modes/walk-mode/walk-mode-hooks";
import {
  isFromSheetObject,
  isPanoObject,
  UnknownObject,
} from "@/object-cache-type-guard";
import { setHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { selectActiveElementId } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { deactivateTool, setAnnotationExpansion } from "@/store/ui/ui-slice";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import { useUpdateVisibilityDistance } from "@/utils/use-update-visibility-distance";
import {
  CreateRectangleAnnotation,
  DrawRectangleControls,
  selectIElementWorldPosition,
  useImmediateControlsLock,
  useOverrideCursor,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID, TypedEvent } from "@faro-lotv/foundation";
import { IElementTypeHint } from "@faro-lotv/ielement-types";
import { sphere3DToEquirectangular2D } from "@faro-lotv/lotv";
import { isIElementVideoModeCopy } from "@faro-lotv/project-source";
import { Dialog } from "@mui/material";
import { useThree } from "@react-three/fiber";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
} from "react";
import { Mesh, Object3D, Vector3 } from "three";
import {
  isPointAnnotationData,
  PointAnnotationData,
} from "../annotation-mutation-utils";
import { ToolCallback, ToolControlsRef } from "../tool-controls-interface";
import {
  AnnotationShape,
  DEFAULT_SHAPE_STYLE,
  StyleKind,
  useAnnotationShapesStore,
  useIsAnnotationShapesEnabled,
} from "../annotation-shapes/annotation-shapes-store";
import { annotationShapeCursor } from "../annotation-shapes/annotation-shapes-cursors";
import { PendingPointMarker } from "../annotation-shapes/pending-point-marker";
import { ShapeDraftRenderer } from "../annotation-shapes/shape-draft-renderer";
import { useShapeDrawing } from "../annotation-shapes/use-shape-drawing";
import { useLensCursor } from "../use-lens-cursor";
import { useZoomLensPicking } from "../use-zoom-lens-picking";
import {
  ImagePointAnnotationData,
  useCreateImagePointAnnotation,
} from "./use-create-image-point-annotation";
import { useCreateModel3dAnnotation } from "./use-create-model-3d-annotation";
import {
  cameraToAnnotationCameraParams,
  useCreateSinglePointAnnotation,
} from "./use-create-single-point-annotation";

type AnnotationToCreate = PointAnnotationData | Mesh | ImagePointAnnotationData;

type AnnotationPickerProps = {
  /** True if this tool is the active one */
  isActive: boolean;

  /** List of active models in the scene that will send events to the tool */
  activeModels: UnknownObject[] | null;

  /** Callback issued when the hovered point changes */
  currentPointChanged?(point: Vector3 | undefined): void;

  /** Optional signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens?: TypedEvent<GUID>;
};

/** @returns the tool to create new annotations */
export const AnnotationPicker = forwardRef<
  ToolControlsRef,
  AnnotationPickerProps
>(function AnnotationPicker(
  { isActive, activeModels, currentPointChanged, onValidClickInZoomLens },
  ref,
): JSX.Element | null {
  const dispatch = useAppDispatch();

  // Whether the annotation is being created while viewing a VideoMode copy
  const { referenceElement } = useCurrentScene();
  const inVideoModeCopy =
    !!referenceElement && isIElementVideoModeCopy(referenceElement);

  // Reset flag each time the component is enabled
  useEffect(() => {
    if (isActive) {
      dispatch(setHasCreatedNewAnnotation(false));
      // Each annotation starts with a continuous line, dashed and cloud are a choice for one shape.
      // The color is kept as the last used one.
      useAnnotationShapesStore
        .getState()
        .setStyle({ outline: DEFAULT_SHAPE_STYLE.outline });
    }
  }, [dispatch, isActive]);

  const [annotation, setAnnotation] = useState<AnnotationToCreate>();
  // The r3f click and the zoom lens signal both fire for one pointer up, and React state
  // is not updated in between, so the ref is what prevents starting two annotations
  const annotationRef = useRef<AnnotationToCreate>(undefined);
  // World position of a point annotation being created, to show its pin while the panel is open
  const [pendingPoint, setPendingPoint] = useState<Vector3>();
  const startAnnotation = useCallback(
    (toCreate?: AnnotationToCreate, point?: Vector3) => {
      annotationRef.current = toCreate;
      setAnnotation(toCreate);
      setPendingPoint(point);
      // A new place or shape is followed by filling in its details, so the phone sheet opens in full
      useAnnotationShapesStore.setState({ isSheetMinimized: false });
    },
    [],
  );

  // With shapes, the creation panel does not block the canvas, so a new click or shape replaces the annotation being created
  const hasShapes = useIsAnnotationShapesEnabled();
  const canStartAnnotation = useCallback(
    () => !annotationRef.current || hasShapes,
    [hasShapes],
  );

  const panoElement = activeModels?.find(isPanoObject)?.iElement;

  const onShapeClosed = useCallback(
    (shape: PointAnnotationData, kind: AnnotationShape) => {
      if (!canStartAnnotation()) return;
      const isNew = !annotationRef.current;
      if (isNew) {
        Analytics.track<CreateAnnotationEventProperties>(
          EventType.createAnnotation,
          {
            shape: kind === AnnotationShape.polygon ? "polygon" : "rectangle",
            inVideoModeCopy,
            via: "scene",
          },
        );
      }
      startAnnotation(shape);
    },
    [inVideoModeCopy, canStartAnnotation, startAnnotation],
  );

  const shapeDrawing = useShapeDrawing({
    isActive,
    panoElement,
    onShapeClosed,
  });
  const saveShapeStyle = useAnnotationShapesStore((s) => s.saveStyle);
  const clearShapeDraft = useAnnotationShapesStore((s) => s.clearDraft);
  const isShapeAnnotation =
    !!annotation &&
    !(annotation instanceof Mesh) &&
    isPointAnnotationData(annotation) &&
    annotation.points.length > 1;
  // Legacy rectangle meshes have no style, points have a color, shapes also a line style
  let styleKind: StyleKind | undefined;
  if (annotation && !(annotation instanceof Mesh)) {
    styleKind = isShapeAnnotation ? "shape" : "point";
  }

  const isZoomLensOn = useAppSelector(selectZoomLensActive);
  // The lens picks on the point cloud behind the 360, which the annotation would not record otherwise
  const acquisitionPanoId = useAppSelector(selectActiveElementId);

  // Function to start a new annotation, either from a point picked in the scene or from a click in the zoom lens.
  // The picked point is the last one hovered, not the one under the cursor at click time.
  const startPointAnnotation = useCallback(
    (
      point: Vector3,
      parentId: GUID,
      type: PointAnnotationData["type"] = IElementTypeHint.spaceAnnotation,
    ) => {
      if (!canStartAnnotation()) return;
      const isNew = !annotationRef.current;

      if (isNew) {
        Analytics.track<CreateAnnotationEventProperties>(
          EventType.createAnnotation,
          {
            shape: "point",
            inVideoModeCopy,
            via: "scene",
          },
        );
      }

      startAnnotation(
        {
          points: [point.toArray()],
          parentId,
          type,
          acquisitionPanoId: isZoomLensOn ? acquisitionPanoId : undefined,
        } satisfies PointAnnotationData,
        point.clone(),
      );
    },
    [
      inVideoModeCopy,
      canStartAnnotation,
      isZoomLensOn,
      acquisitionPanoId,
      startAnnotation,
    ],
  );

  // Get the hover handler from the zoom lens, which will call startPointAnnotation when a valid click is signaled
  const zoomLensPointHovered = useZoomLensPicking({
    isActive,
    currentPointChanged,
    onValidClickInZoomLens,
    onPointPicked: startPointAnnotation,
  });

  const { onHovered: onShapeHovered } = shapeDrawing;
  const pointHovered = useCallback<ToolCallback>(
    (ev, iElementId, features) => {
      zoomLensPointHovered(ev, iElementId, features);
      onShapeHovered(
        ev.point,
        isFromSheetObject(ev.object) ? iElementId : undefined,
      );
    },
    [onShapeHovered, zoomLensPointHovered],
  );

  // Override the cursor while the user can pick: to the lens cursor when the zoom lens is on, otherwise to a crosshair,
  // or with shapes, to a cursor telling what the next click does. Only on the canvas, the creation panel keeps its cursors.
  const shapesContext = useAnnotationShapesStore((s) => s.context);
  const selectedShape = useAnnotationShapesStore((s) => s.shape);
  const canClosePolygon = useAnnotationShapesStore((s) => s.canClosePolygon);
  const canvas = useThreeEventTarget();
  const canPick = isActive && (!annotation || hasShapes);

  // One annotation at a time, so switching shape also removes a point already placed,
  // like it removes a shape already drawn
  const previousShape = useRef(selectedShape);
  useEffect(() => {
    if (previousShape.current === selectedShape) return;
    previousShape.current = selectedShape;
    if (annotationRef.current) startAnnotation(undefined);
  }, [selectedShape, startAnnotation]);
  useOverrideCursor(
    hasShapes && shapesContext
      ? annotationShapeCursor(selectedShape, canClosePolygon)
      : "crosshair",
    canPick && !isZoomLensOn,
    hasShapes ? canvas : undefined,
  );
  useLensCursor(canPick && isZoomLensOn);

  // With shapes, drags never reach here: PickingTools drops clicks where the pointer moved.
  // Without, the drag state of the rectangle controls is used, as it can't get stuck there
  const [isUserDragging, setIsUserDragging] = useState(false);
  const pointClicked = useCallback<ToolCallback>(
    (ev, iElementId) => {
      if (isUserDragging && !hasShapes) return;
      ev.stopPropagation();

      if (shapeDrawing.isDrawingShapes) {
        // On 360s the shape is drawn on the image, through the rectangle controls below
        if (isFromSheetObject(ev.object)) {
          shapeDrawing.onSheetClicked(ev.point, iElementId);
        }
        return;
      }

      startPointAnnotation(
        ev.point,
        iElementId,
        isFromSheetObject(ev.object)
          ? IElementTypeHint.mapAnnotation
          : IElementTypeHint.spaceAnnotation,
      );
    },
    [hasShapes, isUserDragging, shapeDrawing, startPointAnnotation],
  );

  useImperativeHandle(ref, () => ({
    pointClicked,
    pointHovered,
  }));

  const panoMatrixWorldInverse = activeModels
    ?.find(isPanoObject)
    ?.matrixWorld.clone()
    .invert();

  const { isPanoWithDepth } = useIsPanoWithDepthAndReady(panoElement);

  const camera = useThree((s) => s.camera);
  const store = useAppStore();

  const createSinglePointAnnotation = useCreateSinglePointAnnotation();
  const createModel3dAnnotation = useCreateModel3dAnnotation();
  const updateVisibilityDistance = useUpdateVisibilityDistance();
  const createImagePointAnnotation = useCreateImagePointAnnotation();

  const saveAnnotation = useCallback<CreateAnnotationFormProps["onSave"]>(
    async (details) => {
      assert(annotation, "Unable to save an undefined annotation");

      let markupId: GUID;

      if (annotation instanceof Mesh) {
        assert(
          panoElement,
          "A pano is required to create a Model3D Annotation",
        );
        markupId = await createModel3dAnnotation(
          panoElement,
          annotation,
          details,
          cameraToAnnotationCameraParams(camera),
        );
      } else if (isPointAnnotationData(annotation)) {
        markupId = await createSinglePointAnnotation(
          annotation,
          details,
          cameraToAnnotationCameraParams(camera),
        );
      } else {
        markupId = await createImagePointAnnotation(annotation, details);
      }

      if (styleKind) saveShapeStyle(markupId, styleKind);
      clearShapeDraft();

      // Auto expand the new annotation
      dispatch(setAnnotationExpansion({ id: markupId, expanded: true }));

      // Ensure the new generated annotation is visible
      const markupPosition = selectIElementWorldPosition(markupId)(
        store.getState(),
      );
      updateVisibilityDistance(camera, new Vector3().fromArray(markupPosition));
      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.annotations,
          visibility: true,
        }),
      );

      dispatch(setHasCreatedNewAnnotation(true));
      startAnnotation(undefined);
      dispatch(deactivateTool());
    },
    [
      annotation,
      camera,
      clearShapeDraft,
      createModel3dAnnotation,
      createSinglePointAnnotation,
      createImagePointAnnotation,
      dispatch,
      styleKind,
      panoElement,
      saveShapeStyle,
      startAnnotation,
      store,
      updateVisibilityDistance,
    ],
  );

  const setWasDiscarded = useAnnotationShapesStore((s) => s.setWasDiscarded);
  const closeDialog = useCallback(() => {
    startAnnotation(undefined);
    clearShapeDraft();
    setWasDiscarded(true);
  }, [clearShapeDraft, setWasDiscarded, startAnnotation]);

  // Exiting the tool, with Esc or the toolbar, also closes the creation panel
  useEffect(() => {
    if (isActive || !hasShapes) return;
    startAnnotation(undefined);
    clearShapeDraft();
    setWasDiscarded(false);
  }, [clearShapeDraft, hasShapes, isActive, setWasDiscarded, startAnnotation]);

  // With shapes, Esc steps back one stage: a polygon being drawn is cleared by the shape drawing,
  // then an annotation being created is discarded, then the tool is exited
  useEffect(() => {
    if (!isActive || !hasShapes) return;
    function onKeyDown(ev: KeyboardEvent): void {
      if (ev.key !== "Escape" || ev.defaultPrevented) return;
      // Esc in a field of the panel, like a dropdown, belongs to that field
      if (ev.target instanceof HTMLElement && ev.target !== document.body) {
        return;
      }
      const { draft } = useAnnotationShapesStore.getState();
      if (draft && !draft.isClosed) return;
      if (annotationRef.current) closeDialog();
      else dispatch(deactivateTool());
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeDialog, dispatch, isActive, hasShapes]);

  const { setContent } = useUiOverlayContext();
  const setPanelContent = useAnnotationShapesStore((s) => s.setPanelContent);

  useEffect(() => {
    if (!annotation) return;

    // With shapes, annotations are created in a panel next to the canvas, so the shape stays visible
    if (hasShapes) {
      // Color and line style are set from the toolbar, where the shape is drawn
      setPanelContent(
        <CreateAnnotationForm
          onClose={closeDialog}
          onSave={saveAnnotation}
          allowExternalMarkups
        />,
        { title: "New annotation", styleKind },
      );
      return () => setPanelContent(undefined);
    }

    setContent(
      <Dialog
        open
        slotProps={{
          paper: {
            sx: { p: 1, background: "transparent" },
            elevation: 0,
          },
        }}
      >
        <CreateAnnotationForm
          onClose={closeDialog}
          onSave={saveAnnotation}
          allowExternalMarkups
        />
      </Dialog>,
    );

    return () => setContent(undefined);
  }, [
    annotation,
    closeDialog,
    styleKind,
    saveAnnotation,
    setContent,
    setPanelContent,
    hasShapes,
  ]);

  // This callback does  not depend on the 'pano' object, but only on the 'panoElement' and on the 'panoMatrixWorldInverse'
  // matrix. If it depended on 'pano', there would be a problem where all panos ever mounted by this component
  // are still kept alive by the 'boxes' the callbacks create when lambda-capturing the variables.
  // Dropping the old function from the hook does not erase the pointer that an older box already wrote.
  // And because the callbacks have different dependencies, there is always one reused callback holding
  // the previous box. That is why the chain never breaks.
  const onPointAnnotationCreated = useCallback(
    (point: Vector3) => {
      if (!panoElement || !panoMatrixWorldInverse) return;
      if (!canStartAnnotation()) return;
      const isNew = !annotationRef.current;
      const worldPoint = point.clone();
      // Converting point from global to local coordinates to the pano
      point.applyMatrix4(panoMatrixWorldInverse);
      // Converting the local point to the pixel in the pano image
      const pixel = sphere3DToEquirectangular2D(
        point,
        panoElement.pixelWidth,
        panoElement.pixelHeight,
      );
      if (isNew) {
        Analytics.track<CreateAnnotationEventProperties>(
          EventType.createAnnotation,
          { shape: "pano pixel", inVideoModeCopy, via: "scene" },
        );
      }
      // Creating an annotation to the given pano pixel
      startAnnotation(
        {
          pixelPosition: {
            x: Math.round(pixel.x),
            y: Math.round(pixel.y),
          },
          parentId: panoElement.id,
        } satisfies ImagePointAnnotationData,
        worldPoint,
      );
    },
    [
      panoElement,
      panoMatrixWorldInverse,
      inVideoModeCopy,
      canStartAnnotation,
      startAnnotation,
    ],
  );

  const onRectangleAnnotationCreated = useCallback(
    (annotation: Mesh) => {
      Analytics.track<CreateAnnotationEventProperties>(
        EventType.createAnnotation,
        { shape: "rectangle", inVideoModeCopy, via: "scene" },
      );
      startAnnotation(annotation);
    },
    [inVideoModeCopy, startAnnotation],
  );

  const onAnnotationCanceled = useCallback(
    () =>
      Analytics.track<CancelAnnotationCreationEventProperties>(
        EventType.cancelAnnotationCreation,
        { via: "escape key" },
      ),
    [],
  );

  const canTakePixelAnnotations = !isPanoWithDepth;

  const { lock, unlock } = useImmediateControlsLock();

  const onDragStart = useCallback(() => {
    lock();
    setIsUserDragging(true);
  }, [lock]);

  const onDragEnd = useCallback(() => {
    unlock();
    setIsUserDragging(false);
  }, [unlock]);

  // The rectangle controls skip onDragEnd when the pointer moved only a few pixels, which with shapes, where a
  // rectangle has its own tool, left the camera locked and every next click ignored. So unlock on every pointer up.
  // TODO PDI-458: fix DrawRectangleControlsLogic in app-component-toolbox and remove this
  useEffect(() => {
    if (!isActive || !hasShapes) return;
    window.addEventListener("pointerup", unlock);
    return () => window.removeEventListener("pointerup", unlock);
  }, [hasShapes, isActive, unlock]);

  const isDrawingPolygon =
    shapeDrawing.isDrawingShapes &&
    shapeDrawing.shape === AnnotationShape.polygon;
  const isDrawingRectangle =
    shapeDrawing.isDrawingShapes &&
    shapeDrawing.shape === AnnotationShape.rectangle;

  // Moved and scaled by the drag controls; the shape itself is drawn by ShapeDraftRenderer
  const [rectangleHandle] = useState(() => new Object3D());
  const { onRectangleDragged } = shapeDrawing;

  let onPanoPointClicked: ((point: Vector3) => void) | undefined;
  if (isDrawingPolygon) {
    onPanoPointClicked = shapeDrawing.onPanoClicked;
  } else if (canTakePixelAnnotations) {
    onPanoPointClicked = onPointAnnotationCreated;
  }

  return (
    <>
      {isActive && isDrawingRectangle && (
        <DrawRectangleControls
          mesh={rectangleHandle}
          isEnabled
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onRectangleUpdated={(rectangle) =>
            rectangle ? onRectangleDragged(rectangle, false) : clearShapeDraft()
          }
          onRectangleCreated={(rectangle) =>
            onRectangleDragged(rectangle, true)
          }
        />
      )}
      {isActive && panoElement && !isDrawingRectangle && (
        <CreateRectangleAnnotation
          // With shapes, rectangles have their own tool, so a click with a small drag stays a point
          isRectangleCreationEnabled={
            !hasShapes && panoElement.typeHint !== IElementTypeHint.odometryPath
          }
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onCreated={onRectangleAnnotationCreated}
          onCancelled={onAnnotationCanceled}
          onPointClicked={onPanoPointClicked}
        />
      )}
      {isActive && <ShapeDraftRenderer />}
      {hasShapes && isActive && annotation && pendingPoint && (
        <PendingPointMarker position={pendingPoint} />
      )}
      {annotation instanceof Mesh && <primitive object={annotation} />}
    </>
  );
});
